import React, { useState, useEffect } from "react";
import api from "../services/api";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function Accounts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600px

  const [formData, setFormData] = useState({
    businessType: "",
    name: "",
    date: "",
    address: "",
    mobile: "",
    email: "",
    paymentTermsDays: "",
    creditLimit: "",
  });

  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await api.get("/accounts");
      setAccounts(res.data);
      setFilteredAccounts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", {
        SubAccount_Name: formData.name,
        BusinessType: formData.businessType,
        Email: formData.email || null,
        Mobile: formData.mobile || null,
        Address: formData.address || null,
        Date: formData.date || null,
        PaymentTermsDays: formData.paymentTermsDays || null,
        CreditLimit: formData.creditLimit || null,
      });

      setFormData({
        businessType: "",
        name: "",
        date: "",
        address: "",
        mobile: "",
        email: "",
        paymentTermsDays: "",
        creditLimit: "",
      });
      fetchAccounts();
    } catch (err) {
      console.error(err);
      alert("Failed to save account");
    }
  };

  const handleEdit = (acc) => {
    setSelectedAccount(acc);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/accounts/${selectedAccount.id}`, {
        SubAccount_Name: selectedAccount.SubAccount_Name,
        BusinessType: selectedAccount.BusinessType,
        Email: selectedAccount.Email || null,
        Mobile: selectedAccount.Mobile || null,
        Address: selectedAccount.Address || null,
        Date: selectedAccount.Date || null,
        PaymentTermsDays: selectedAccount.PaymentTermsDays ?? null,
        CreditLimit: selectedAccount.CreditLimit ?? null,
      });
      setEditDialogOpen(false);
      fetchAccounts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await api.delete(`/accounts/${id}`);
      fetchAccounts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    const filtered = accounts.filter(
      (acc) =>
        (acc.SubAccount_Name || "")
          .toLowerCase()
          .includes(term.toLowerCase()) ||
        (acc.BusinessType || "")
          .toLowerCase()
          .includes(term.toLowerCase()) ||
        (acc.Email || "").toLowerCase().includes(term.toLowerCase()) ||
        (acc.Mobile || "").toLowerCase().includes(term.toLowerCase())
    );
    setFilteredAccounts(filtered);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Add Form */}
      <Paper sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 2 }}>
        <Typography variant="h5" textAlign="center" mb={3}>
          Accounts Form
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Business Type"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                fullWidth
                required
              >
                <MenuItem value="Supplier">Supplier</MenuItem>
                <MenuItem value="Customer">Customer</MenuItem>
                <MenuItem value="Employee">Employee</MenuItem>
                <MenuItem value="Expense">Expense</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Payment terms (days)"
                name="paymentTermsDays"
                type="number"
                value={formData.paymentTermsDays}
                onChange={handleChange}
                fullWidth
                helperText="e.g. 30 for Net 30 (blank = due on invoice date)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Credit limit ($)"
                name="creditLimit"
                type="number"
                value={formData.creditLimit}
                onChange={handleChange}
                fullWidth
                helperText="Flag when they owe more than this (blank = no limit)"
              />
            </Grid>
            <Grid size={{ xs: 12 }} textAlign="center">
              <Button type="submit" variant="contained">
                Save
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Search */}
      <Box mt={3}>
        <TextField
          label="Search..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          fullWidth
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Business Type</TableCell>
              <TableCell>Name</TableCell>
              {!isMobile && <TableCell>Address</TableCell>}
              {!isMobile && <TableCell>Mobile</TableCell>}
              {!isMobile && <TableCell>Email</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell>{acc.id}</TableCell>
                <TableCell>{acc.BusinessType}</TableCell>
                <TableCell>{acc.SubAccount_Name}</TableCell>
                {!isMobile && <TableCell>{acc.Address}</TableCell>}
                {!isMobile && <TableCell>{acc.Mobile}</TableCell>}
                {!isMobile && <TableCell>{acc.Email}</TableCell>}
                <TableCell>
                  <Button onClick={() => handleEdit(acc)}>Edit</Button>
                  <Button color="error" onClick={() => handleDelete(acc.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Business Type"
                  value={selectedAccount.BusinessType || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, BusinessType: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Name"
                  value={selectedAccount.SubAccount_Name || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, SubAccount_Name: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Address"
                  value={selectedAccount.Address || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, Address: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Mobile"
                  value={selectedAccount.Mobile || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, Mobile: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Email"
                  value={selectedAccount.Email || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, Email: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={selectedAccount.Date || ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, Date: e.target.value })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Payment terms (days)"
                  type="number"
                  value={selectedAccount.PaymentTermsDays ?? ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, PaymentTermsDays: e.target.value })
                  }
                  fullWidth
                  helperText="e.g. 30 for Net 30"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Credit limit ($)"
                  type="number"
                  value={selectedAccount.CreditLimit ?? ""}
                  onChange={(e) =>
                    setSelectedAccount({ ...selectedAccount, CreditLimit: e.target.value })
                  }
                  fullWidth
                  helperText="Blank = no limit"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
